import {
    SearchFilters, SearchSortOrder, SearchQuery, SearchColumn,
    NestedFilterObject, SearchAggregations,
} from '../../store/common/search/types';

const listOfValidColumnFields: string[] = [
    'name',
    'type',
    'sortField',
    'nested',
    'order',
];

export interface AppSpecifiedSearchQueryBuildService {
    getColumnsDefinition: (dataType: string, featureId?: string) => [];
    appendSubmissionStatusFilter?: (searchQuery: SearchQuery, includeDeletedDeliveries?: boolean) => SearchQuery;
    [key: string]: (args: any) => any;
}

let appSpecifiedSearchQueryBuildService: AppSpecifiedSearchQueryBuildService;

const commonSearchQueryBuilder = {
    cleanQuery(searchJSON: string): string {
        const query: SearchQuery = JSON.parse(decodeURIComponent(searchJSON));
        const newColumns: SearchColumn[] = query.columns.map((column: SearchColumn) => {
            Object.keys(column).forEach((key: string) => {
                if (!listOfValidColumnFields.some((field) => field === key)) {
                    delete (column as any)[key];
                }
            });
            return column;
        });
        return encodeURIComponent(JSON.stringify({ ...query, columns: newColumns }));
    },
    encodeQueriesForMultiSearch(searchJSONs: string[]): string {
        const decodedQueries: string[] = searchJSONs.map((searchJSON) => decodeURIComponent(searchJSON));
        const multiQuery = `[${decodedQueries}]`;
        return encodeURIComponent(multiQuery);
    },

    encodeQuery(query: SearchQuery): string {
        return encodeURIComponent(JSON.stringify(query));
    },

    createEmptyQuery(): SearchQuery {
        return {
            page: 1,
            hitsPerPage: 200,
            searchText: '',
            filters: {},
            columns: [],
            aggregations: {},
        };
    },
    createQueryWithFilterAndSort(
        filters: SearchFilters,
        sortName: string,
        order: SearchSortOrder,
        hitsPerPage?: number,
        aggregations?: SearchAggregations
    ): SearchQuery {
        let query: SearchQuery = this.createEmptyQuery();
        query = this.replaceFilters(query, filters);
        query = this.setSortColumn(query, sortName, order);
        if (hitsPerPage) {
            query = this.setHitsPerPage(query, hitsPerPage);
        }
        if (aggregations) {
            query = this.setAggregation(query, aggregations);
        }
        return query;
    },
    replaceFilters(query: SearchQuery, filters: SearchFilters): SearchQuery {
        return { ...query, filters };
    },
    appendSource(query: SearchQuery, featureId: string): SearchQuery {
        return {
            ...query,
            source: featureId,
        };
    },
    appendFilter(query: SearchQuery, filterKey: string, filterValue: any): SearchQuery {
        return {
            ...query,
            filters: {
                ...query.filters,
                [filterKey]: filterValue,
            },
        };
    },
    appendFilters(query: SearchQuery, newFilters: SearchFilters): SearchQuery {
        return {
            ...query,
            filters: {
                ...query.filters,
                ...newFilters,
            },
        };
    },
    appendNestedFilter(query: SearchQuery, newFilter: NestedFilterObject): SearchQuery {
        return {
            ...query,
            filters: {
                $nested: {
                    ...query.filters.$nested,
                    ...newFilter,
                },
            },
        };
    },
    removeFilter(query: SearchQuery, filterKey: string): SearchQuery {
        const updatedFilters: SearchFilters = { ...query.filters };
        delete updatedFilters[filterKey];
        return {
            ...query,
            filters: updatedFilters,
        };
    },
    createNestedFilter(fieldName: string, value: any): NestedFilterObject {
        const $path: string = fieldName.split('.')[0];
        return {
            $path,
            [fieldName]: value,
        };
    },
    /**
     * Will first remove the oldFilterPart from the query before adding the newFilterPart
     * @param query
     * @param oldFilterPart
     * @param newFilterPart
     */
    replaceFilterPart(query: SearchQuery, oldFilterPart: SearchFilters, newFilterPart: SearchFilters) {
        const filtersWithoutOld = { ...query.filters };
        if (oldFilterPart) {
            Object.keys(oldFilterPart).forEach((filterKey: string) => {
                delete filtersWithoutOld[filterKey];
            });
        }
        return {
            ...query,
            filters: { ...filtersWithoutOld, ...newFilterPart },
        };
    },
    sortNestedFilteredColumn(query: SearchQuery): SearchQuery {
        const existingColumns: SearchColumn[] = query.columns.slice(0);
        const newColumns: SearchColumn[] = existingColumns.map((column: SearchColumn) => {
            if (column.order && column.nested) {
                return {
                    ...column,
                    sortField: column.nested.join('.'),
                };
            }
            return column;
        });
        return {
            ...query,
            columns: newColumns,
        };
    },
    /**
     * Will set the provided columnName as the sorted column and set to 'asc'. If column is sorted on already,
     * the order will be toggled between 'asc' and 'desc'.
     * @param query
     * @param columnName
     */
    toggleSortColumn(query: SearchQuery, columnName: string): SearchQuery {
        const existingColumns: SearchColumn[] = query.columns.slice(0);
        const newColumns: SearchColumn[] = existingColumns.map((column: SearchColumn) => {
            if (column.name === columnName) {
                const columnOrder: SearchSortOrder = column.order === 'asc' ? 'desc' : 'asc';
                return { ...column, order: columnOrder };
            }
            const { order, ...newColumn } = column; // eslint-disable-line @typescript-eslint/no-unused-vars
            return newColumn;
        });
        return {
            ...query,
            columns: newColumns,
        };
    },
    setSortColumn(query: SearchQuery, columnName: string, columnOrder: SearchSortOrder): SearchQuery {
        const existingColumns: SearchColumn[] = query.columns.slice(0);
        let columnExists = false;
        const newColumns: SearchColumn[] = existingColumns.map((column: SearchColumn) => {
            if (column.name === columnName || column.ref === columnName) {
                columnExists = true;
                return { ...column, order: columnOrder };
            }
            const { order, ...newColumn } = column; // eslint-disable-line @typescript-eslint/no-unused-vars
            return newColumn;
        });
        if (!columnExists) {
            newColumns.push({
                name: columnName,
                order: columnOrder,
            });
        }
        return {
            ...query,
            columns: newColumns,
        };
    },
    replaceColumns(query: SearchQuery, columns: SearchColumn[] = [], dataType?: string): SearchQuery {
        const defs: SearchColumn[] = appSpecifiedSearchQueryBuildService.getColumnsDefinition(dataType);
        const parsedColumns: SearchColumn[] = columns?.length ? columns?.map((column: any) => {
            const col: SearchColumn = defs.find((def: SearchColumn) => def.ref === column.ref);
            return { ...col, ...column };
        }) : [];
        return {
            ...query,
            columns: parsedColumns,
        };
    },
    setSearchText(query: SearchQuery, searchText: string, textFields: any[] = []): SearchQuery {
        const searchData = {
            ...query,
            searchText,
        };
        if (textFields.length) {
            searchData.searchTextFields = textFields;
        }
        return searchData;
    },
    setSearchTextFIelds(query: SearchQuery, searchTextFields: string[]): SearchQuery {
        return {
            ...query,
            searchTextFields,
        };
    },
    setHitsPerPage(query: SearchQuery, hitsPerPage: number): SearchQuery {
        return {
            ...query,
            hitsPerPage,
            page: 1,
        };
    },
    setAggregation(query: SearchQuery, aggregations: SearchAggregations): SearchQuery {
        return {
            ...query,
            aggregations,
        };
    },
    setPage(query: SearchQuery, page: number): SearchQuery {
        return {
            ...query,
            page,
        };
    },
    /**
     * Returns two queries that can be run in sequence in order to center the filterKey with a given filterValue in the
     * a hits array when combining data from the two queries. As the beforeQuery orders the hits descending, the hits
     * from this query has to be reversed before combined
     * @param query
     * @param filterKey
     * @param filterValue
     * @param hitsBeforeAfter The number of hits in each query
     */
    centerKeyInHits(query: SearchQuery, filterKey: string, filterValue: any, hitsBeforeAfter: number): SearchQuery[] {
        const descendingOrdered: SearchQuery = this.setSortColumn(query, filterKey, 'desc');
        const beforeQuery: SearchQuery = {
            ...descendingOrdered,
            hitsPerPage: hitsBeforeAfter,
            filters: {
                ...descendingOrdered.filters,
                [filterKey]: {
                    lt: filterValue,
                },
            },
        };
        const ascendingOrdered: SearchQuery = this.setSortColumn(query, filterKey, 'asc');
        const afterQuery: SearchQuery = {
            ...ascendingOrdered,
            hitsPerPage: hitsBeforeAfter + 1,
            filters: {
                ...ascendingOrdered.filters,
                [filterKey]: {
                    gte: filterValue,
                },
            },
        };
        return [beforeQuery, afterQuery];
    },
};

export const initializedCommonSearchQueryBuilderService = (appSearchQueryBuilder: AppSpecifiedSearchQueryBuildService) => {
    appSpecifiedSearchQueryBuildService = appSearchQueryBuilder;
};

export const getAppSpecifiedSearchQueryBuilderService = (): AppSpecifiedSearchQueryBuildService => {
    return appSpecifiedSearchQueryBuildService;
};

export default commonSearchQueryBuilder;
